
import Vue, { PropType } from 'vue'
import Opz2Icon from './Opz2Icon.vue'
import Opz2Cards from './Opz2Cards.vue'
import Opz2Table from './Opz2Table.vue'
import { Report, Row, cnt, fnPct, has, longDate, periodLabel, shortDate } from './model'

/**
 * stat_by_date_data_full / total_stat_data_full функции как есть.
 * key — поле строки дня, total — поле итога total_stat_data_full.
 */
const COLUMNS = [
    { label: 'Дата', key: 'visit_date', width: 170 },
    { label: 'Визитов', key: 'client_count', total: 'total_visits_count', cls: 'num' },
    { label: 'ОПЗ', key: 'recorded_same_spec_cnt', total: 'total_recorded_same_spec_cnt', cls: 'num' },
    {
        label: 'Кросс-продаж',
        key: 'recorded_other_spec_cnt',
        total: 'total_recorded_other_spec_cnt',
        cls: 'num',
    },
    { label: 'Отмен', key: 'recorded_cancel_cnt', total: 'total_recorded_cancel_cnt', cls: 'num' },
    {
        label: 'Доля перезаписи',
        key: 'recorded_other_day_proportion',
        total: 'total_recorded_other_day_proportion',
        cls: 'num',
        cellCls: 'num value-indigo',
        width: 166,
        pct: true,
    },
]

export default Vue.extend({
    name: 'Opz2DaysPane',
    components: { Opz2Icon, Opz2Cards, Opz2Table },
    props: {
        report: { type: Object as PropType<Report>, required: true },
        emptyLabel: { type: String, default: 'В этом периоде нет визитов' },
    },
    data: () => ({ columns: COLUMNS as any[] }),
    computed: {
        days(): Row[] {
            return this.report.days.rows
        },
        total(): Row {
            return this.report.days.total
        },
        period(): string {
            return periodLabel(this.report.period.from, this.report.period.to)
        },
        /** ОПЗ и кросс-продажи по дням есть в выдаче — график двумя цветами, иначе — «перезаписались» */
        stacked(): boolean {
            return this.days.some(d => has(d.recorded_same_spec_cnt) || has(d.recorded_other_spec_cnt))
        },
        chartLabel(): string {
            return this.stacked ? 'ОПЗ и кросс-продажи по дням' : 'Перезаписавшиеся клиенты по дням'
        },
        cards(): any[] {
            const t = this.total
            return [
                { label: 'Всего визитов', value: cnt(t.total_visits_count), note: 'За выбранный период' },
                {
                    label: 'Визиты с перезаписями',
                    value: cnt(t.total_recorded_other_day),
                    note: `${cnt(t.total_recorded_same_spec_cnt)} ОПЗ + ${cnt(
                        t.total_recorded_other_spec_cnt
                    )} кросс-продаж`,
                    color: 'indigo',
                },
                {
                    label: 'Записей без отмен',
                    value: cnt(t.total_recorded_active_cnt),
                    note: `${cnt(t.total_recorded_cancel_cnt)} отмен`,
                    color: 'teal',
                },
                {
                    label: 'Доля перезаписи',
                    value: fnPct(t.total_recorded_other_day_proportion),
                    note: 'Перезаписались / визиты',
                },
            ]
        },
        bars(): any[] {
            const days = this.days
            const value = (v: unknown) => Number(v) || 0
            const first = (d: Row) => value(this.stacked ? d.recorded_same_spec_cnt : d.recorded_other_day)
            const second = (d: Row) => (this.stacked ? value(d.recorded_other_spec_cnt) : 0)
            const max = Math.max(1, ...days.map(d => first(d) + second(d)))
            const bw = 1000 / days.length
            return days.map((d, i) => {
                const fh = (first(d) / max) * 86
                const sh = (second(d) / max) * 86
                return {
                    day: d.visit_date,
                    title: this.stacked
                        ? `${shortDate(d.visit_date)}: ОПЗ ${cnt(
                              d.recorded_same_spec_cnt
                          )}, кросс-продаж ${cnt(d.recorded_other_spec_cnt)}`
                        : `${shortDate(d.visit_date)}: перезаписались ${cnt(d.recorded_other_day)} из ${cnt(
                              d.client_count
                          )}`,
                    x: i * bw + 4,
                    width: Math.max(2, bw - 9),
                    firstY: 98 - fh,
                    firstH: fh,
                    secondY: 98 - fh - sh,
                    secondH: sh,
                }
            })
        },
    },
    methods: {
        cnt,
        fnPct,
        shortDate,
        // в таблице год не нужен, если период в пределах одного года
        dayLabel(day: string) {
            return longDate(day).replace(/ \d{4}$/, '')
        },
    },
})
